import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Clock,
  Search,
  Sparkles,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { usePublishedBlogs } from "../../hooks/useBlogs";
import { CardSkeleton } from "../../components/Skeletons/CardSkeleton";
import { ErrorState, EmptyState } from "../../components/States/ErrorState";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1504711434969-e33886168d8c?auto=format&fit=crop&w=900&q=80";

const BusiMagHero = ({ articleCount, heroArticle }) => (
  <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden hero-mesh">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-violet-300/20 rounded-full blur-[120px] animate-float-orb" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-300/15 rounded-full blur-[120px] animate-float-orb-reverse" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-sm text-slate-600">
            <BookOpen className="w-4 h-4 text-violet-600" /> BusiMag
          </span>
          <h1 className="mt-6 sm:mt-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1]">
            Insights for{" "}
            <span className="text-gradient-vibrant">ambitious businesses</span>
          </h1>
          <p className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-500 max-w-xl leading-relaxed">
            Search and browse backend-powered articles published from your admin
            panel.
          </p>
        </div>

        <div className="relative mt-2 lg:mt-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-violet-200/30 animate-morph-blob" />
          <div className="relative">
            <img
              src={heroArticle?.image || FALLBACK_IMAGE}
              alt={heroArticle?.title || "Magazine editorial"}
              className="w-full rounded-3xl shadow-apple-xl card-shine object-cover aspect-[4/3]"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
            <div className="absolute -bottom-4 -left-4 glass rounded-2xl px-4 py-3 shadow-apple-xl flex items-center gap-3 max-w-[90%]">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 line-clamp-1">
                  {heroArticle?.title || `${articleCount}+ Articles`}
                </p>
                <p className="text-[10px] text-slate-500 line-clamp-1">
                  {heroArticle?.category
                    ? `Hero follows latest featured post in admin (${heroArticle.category})`
                    : "Published from admin panel"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FeaturedSection = ({ articles }) => {
  const featured = articles.filter((article) => article.featured).slice(0, 2);

  if (!featured.length) {
    return null;
  }

  return (
    <section className="relative py-12 sm:py-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-sm font-semibold text-violet-600 tracking-widest uppercase mb-8">
          Featured
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {featured.map((article, i) => (
            <motion.article
              key={article.slug || article.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group relative glass card-glow-top card-shine rounded-2xl overflow-hidden shadow-apple hover:shadow-apple-xl transition-all duration-500"
            >
              <Link to={`/busimag/${article.slug}`}>
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={article.image || FALLBACK_IMAGE}
                    alt={article.title}
                    className="w-full h-full object-cover img-parallax transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2.5 py-1 rounded-full glass-strong text-[10px] font-bold text-violet-700">
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-white/70">
                        <Clock className="w-3 h-3" /> {article.readTime}
                      </span>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-white leading-snug group-hover:text-violet-200 transition-colors">
                      {article.title}
                    </h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-500">
                      {article.author} • {article.date}
                    </div>
                    <span className="text-xs font-semibold text-violet-600 group-hover:text-violet-700 flex items-center gap-1 transition-colors">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

const NewsletterCTA = () => (
  <section className="relative py-16 sm:py-24 overflow-hidden">
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="card-shine rounded-3xl shadow-apple-xl glass overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <div className="inline-flex p-3 rounded-full bg-violet-50 mb-5 w-fit">
              <Sparkles className="w-6 h-6 text-violet-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">
              Stay ahead of the curve
            </h2>
            <p className="text-base sm:text-lg text-slate-500 mb-6 sm:mb-8 leading-relaxed">
              Get weekly insights on AI, growth strategies, and digital
              transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="your@email.com"
                className="flex-1 px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all text-sm shadow-sm"
              />
              <button className="btn-shine px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold hover:shadow-apple-lg transition-all whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>

          <div className="hidden lg:block relative">
            <img
              src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=600&q=80"
              alt="Newsletter reading"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const BusiMagPage = () => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch main blog articles with pagination, search, and filters
  const {
    data: blogsData = {},
    isLoading,
    error,
  } = usePublishedBlogs({
    page: currentPage,
    limit: 9,
    search,
    category: activeCategory === "All" ? "" : activeCategory,
  });

  const articles = blogsData.items || [];
  const meta = blogsData.meta || { page: 1, totalPages: 1, totalItems: 0 };

  // Fetch hero article (featured or latest)
  const { data: heroArticlesData = [] } = usePublishedBlogs({
    page: 1,
    limit: 1,
    hero: true,
  });

  const heroArticle = heroArticlesData[0] || null;

  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(articles.map((article) => article.category)),
    );
    return ["All", ...unique];
  }, [articles]);

  const handleSearchChange = (event) => {
    setCurrentPage(1);
    setSearch(event.target.value.trim());
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
      <Header />
      <BusiMagHero
        articleCount={meta.totalItems || articles.length}
        heroArticle={heroArticle}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <form
          onSubmit={(e) => e.preventDefault()}
          className="flex flex-col md:flex-row gap-3"
        >
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={handleSearchChange}
              placeholder="Search title, excerpt, content..."
              className="w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </form>

        <div className="scroll-pills sm:flex sm:flex-wrap sm:gap-2 mt-5">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => handleCategoryChange(category)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                activeCategory === category
                  ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-apple"
                  : "glass text-slate-600 hover:text-violet-700"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {!isLoading && !error && <FeaturedSection articles={articles} />}

      <section className="relative py-12 sm:py-16 bg-violet-tint overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-8">
            All Articles
          </h2>

          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          )}

          {!isLoading && error && (
            <ErrorState
              title="Failed to load articles"
              description={error || "Something went wrong while fetching articles. Please try again."}
              onRetry={() => window.location.reload()}
            />
          )}

          {!isLoading && !error && articles.length === 0 && (
            <EmptyState
              title="No articles found"
              description="Try adjusting your search filters or check back later for new content."
              icon="BookOpen"
            />
          )}

          {!isLoading && !error && articles.length > 0 && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <article
                    key={article.slug || article.id}
                    className="group glass card-glow-top card-shine rounded-2xl overflow-hidden shadow-apple hover:shadow-apple-lg transition-all duration-500"
                  >
                    <Link to={`/busimag/${article.slug}`}>
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.image || FALLBACK_IMAGE}
                          alt={article.title}
                          className="w-full h-full object-cover img-parallax transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full glass-strong text-[10px] font-bold text-violet-700">
                          {article.category}
                        </span>
                      </div>
                      <div className="p-5">
                        <h3 className="text-base font-bold text-slate-900 mb-2 leading-snug group-hover:text-violet-700 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 mb-4">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">
                            {article.date}
                          </span>
                          <span className="flex items-center gap-1 text-[11px] text-slate-400">
                            <Clock className="w-3 h-3" /> {article.readTime}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Page {meta.page || 1} of {meta.totalPages || 1}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((previous) => Math.max(1, previous - 1))
                    }
                    disabled={!meta.hasPreviousPage}
                    className="inline-flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-semibold bg-white border border-slate-200 text-slate-700 disabled:opacity-50"
                  >
                    <ArrowLeft className="w-4 h-4" /> Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((previous) => previous + 1)}
                    disabled={!meta.hasNextPage}
                    className="inline-flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-semibold bg-white border border-slate-200 text-slate-700 disabled:opacity-50"
                  >
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <NewsletterCTA />
      <Footer />
    </div>
  );
};

export default BusiMagPage;
